import {
  CalendarMonth,
  ExpandLess,
  ExpandMore,
  Groups,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { DiaryEntry, getCompanyDiary } from "src/apis/diary";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import { isUserAdministrator } from "src/utils/auth";
import { formatDateToBR } from "src/utils/date";

const monthFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "short",
});

const formatTimelineLabel = (isoDate: string): { day: string; year: string } => {
  if (!isoDate) return { day: "--", year: "" };
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return { day: isoDate, year: "" };
  return {
    day: monthFormatter.format(date).replace(".", "").toUpperCase(),
    year: String(date.getFullYear()),
  };
};

const ActionLogbook: FC = (): JSX.Element => {
  const theme = useTheme();
  const isAdmin = isUserAdministrator();

  const [entries, setEntries] = useState<DiaryEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});

  const toggleCollapsed = (id: number) =>
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const data = await getCompanyDiary();
      setEntries(data);
    } catch (err: any) {
      setError(err?.message ?? "Não foi possível carregar o diário de campo.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Diário de Campo</Typography>
        <Typography variant="body2" color="text.secondary">
          Acompanhe as ações realizadas pela Eezycare na empresa.
        </Typography>
      </Box>

      {Boolean(error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading && <SkeletonLoad variant="cards" rows={3} />}

      {!isLoading && entries.length === 0 && (
        <Alert severity="info">Nenhuma ação registrada ainda.</Alert>
      )}

      {!isLoading && entries.length > 0 && (
        <Stack spacing={2}>
          {entries.map((entry) => {
            const { day, year } = formatTimelineLabel(entry.data);
            const isCollapsed = isAdmin && Boolean(collapsed[entry.id]);
            return (
              <Box key={entry.id} sx={{ display: "flex", gap: 2 }}>
                <Box
                  sx={{
                    minWidth: 64,
                    textAlign: "right",
                    pt: 1,
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    {day}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {year}
                  </Typography>
                </Box>

                <Paper
                  elevation={1}
                  sx={{
                    flex: 1,
                    p: 2,
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="overline" color="primary" fontWeight="bold">
                        Ação realizada
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {entry.titulo}
                      </Typography>
                      {isAdmin && Boolean(entry.empresa) && (
                        <Typography variant="body2" color="text.secondary">
                          {entry.empresa}
                        </Typography>
                      )}
                    </Box>

                    {isAdmin && (
                      <Tooltip title={isCollapsed ? "Expandir" : "Recolher"}>
                        <IconButton
                          size="small"
                          onClick={() => toggleCollapsed(entry.id)}
                        >
                          {isCollapsed ? <ExpandMore /> : <ExpandLess />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {Boolean(entry.mensagem) && (
                        <Typography variant="body2">{entry.mensagem}</Typography>
                      )}

                      <Stack
                        direction="row"
                        spacing={2}
                        flexWrap="wrap"
                        rowGap={0.5}
                        sx={{ color: "text.secondary" }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <CalendarMonth fontSize="small" />
                          <Typography variant="body2">
                            {formatDateToBR(entry.data)}
                          </Typography>
                        </Box>

                        {entry.numero_participantes != null && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Groups fontSize="small" />
                            <Typography variant="body2">
                              {entry.numero_participantes} participantes
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </Collapse>
                </Paper>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default ActionLogbook;
